import { DndContext } from '@dnd-kit/core';
import {
  useCreateUnlimitedGoalsAllowed,
  useGoalControls,
  useGoals,
} from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useUserHadTrial } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalListCard } from 'shared/components/ui/goal-list-card';
import { GoalListSection } from 'shared/components/ui/goal-list-section';
import { GoalPremiumBanner } from 'shared/components/ui/goal-premium-banner';
import { Section, Sizes } from 'shared/components/ui/section';
import { SortableOverlay } from 'shared/components/ui/sortable-overlay';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useDroppableSectionControls } from 'shared/hooks/use-droppable-section-controls';
import { useSectionCollisionDetection } from 'shared/hooks/use-section-collision-detection';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';

import { useGoalLifeAreaReorder } from '../../hooks/use-goal-life-area-reorder';
import { useLifeAreaActiveGoalSections } from '../../hooks/use-life-area-active-goal-sections';
import { GoalsFilterOptions, goalsFilters } from '../../utils/goals-filter';
import { EmptyBanner } from '../empty-banner';
import { LifeAreaSection } from '../life-area-section';
import * as Styled from './life-area-grouping-goal-list.style';

export type LifeAreaGroupingGoalListProps = {};

export const LifeAreaGroupingGoalList: React.FC<
  LifeAreaGroupingGoalListProps
> = () => {
  const today = useToday();
  const { t } = useTranslation();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const userHadTrial = useUserHadTrial();
  const { onUpdateGoalProgress } = useGoalControls(goals);

  const completedGoals = useMemo(
    () =>
      goalsFilters[GoalsFilterOptions.Completed](goals, today).sort(
        (goalA, goalB) =>
          goalB.completedAt!.getTime() - goalA.completedAt!.getTime(),
      ),
    [goals, today],
  );

  const archivedGoals = useMemo(
    () =>
      goalsFilters[GoalsFilterOptions.Archived](goals, today).sort(
        (goalA, goalB) =>
          goalB.archivedAt!.getTime() - goalA.archivedAt!.getTime(),
      ),
    [goals, today],
  );

  const { sections: initialSections, goals: activeGoals } =
    useLifeAreaActiveGoalSections();
  const onDragEnd = useGoalLifeAreaReorder(initialSections);

  const {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    activeId,
    sections,
  } = useDroppableSectionControls({ initialSections, onDragEnd });

  const sensors = useSortableSensors();
  const collisionStrategy = useSectionCollisionDetection(activeId, sections);
  const openPremiumDialog = useOpenPremiumDialog();

  const {
    allowed: createGoalAllowed,
    enforced: createGoalEnforced,
    limit: createGoalLimit,
    total: createGoalTotal,
  } = useCreateUnlimitedGoalsAllowed();

  const showPremiumBanner =
    !createGoalAllowed ||
    (!createGoalEnforced && createGoalLimit < createGoalTotal);

  const activeItem = useMemo(
    () => (activeId ? goals.find((goal) => goal.id === activeId) : undefined),
    [activeId, goals],
  );

  return (
    <>
      <DndContext
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        sensors={sensors}
        collisionDetection={collisionStrategy}
      >
        {!activeGoals.length ? (
          <EmptyBanner />
        ) : (
          <Styled.Sections>
            {sections.map(({ id, items }) => (
              <Styled.Section key={id}>
                <LifeAreaSection
                  droppableId={id}
                  goals={items}
                  allGoals={goals}
                  lifeAreas={lifeAreas}
                  onPremium={openPremiumDialog}
                  onUpdateProgress={onUpdateGoalProgress}
                  hideLifeArea
                  canReorder
                />
              </Styled.Section>
            ))}
          </Styled.Sections>
        )}

        {!!completedGoals.length && (
          <Section
            title={t('pages.goals.completed.title')}
            titleCount={completedGoals.length}
            size={Sizes.Small}
            canCollapse
          >
            <Styled.CompletedList>
              <GoalListSection
                goals={completedGoals}
                allGoals={goals}
                lifeAreas={lifeAreas}
                onPremium={openPremiumDialog}
                onUpdateProgress={onUpdateGoalProgress}
              />
            </Styled.CompletedList>
          </Section>
        )}

        {!!archivedGoals.length && (
          <Section
            title={t('pages.goals.archived.title')}
            titleCount={archivedGoals.length}
            size={Sizes.Small}
            canCollapse
          >
            <Styled.CompletedList>
              <GoalListSection
                goals={archivedGoals}
                allGoals={goals}
                lifeAreas={lifeAreas}
                onPremium={openPremiumDialog}
                onUpdateProgress={onUpdateGoalProgress}
              />
            </Styled.CompletedList>
          </Section>
        )}

        <SortableOverlay>
          {!!activeItem && (
            <GoalListCard
              goal={activeItem}
              lifeAreas={lifeAreas}
              goals={goals}
              hideLifeArea
            />
          )}
        </SortableOverlay>
      </DndContext>

      {showPremiumBanner && (
        <GoalPremiumBanner
          onClick={openPremiumDialog}
          createGoalEnforced={createGoalEnforced}
          createGoalTotal={createGoalTotal}
          createGoalLimit={createGoalLimit}
          hadTrial={userHadTrial}
        />
      )}
    </>
  );
};
